import styles from './LoadingSkeleton.module.css';

export interface LoadingSkeletonProps {
    /** How many placeholder lines to reserve. Defaults to three. */
    readonly lines?: number;
    /** What is loading, for a screen reader — "Loading deals", not "Loading". */
    readonly label?: string;
    readonly 'data-testid'?: string;
}

/**
 * §5's loading state: fixed-height skeleton blocks on `surface/card-warm`.
 *
 * Blocks rather than a spinner, per `coding-standards.md §3.2` and the FS-1 RouteSkeleton this
 * generalises: a spinner gives no hint of the shape that is coming, and a zero-height fallback
 * makes the page jump when the real content lands. The height is derived from the body type scale
 * rather than picked, so a placeholder line occupies exactly the space its real line will.
 *
 * `role="status"` with an `aria-label` and the blocks marked `aria-hidden` — so the wait is
 * announced once, as a sentence, instead of a screen reader finding a stack of empty boxes.
 */
export function LoadingSkeleton({
    lines = 3,
    label = 'Loading',
    'data-testid': testId,
}: LoadingSkeletonProps) {
    return (
        <div
            aria-busy="true"
            aria-label={label}
            className={styles.skeleton}
            data-testid={testId}
            role="status"
        >
            {Array.from({ length: lines }, (_, index) => (
                <span
                    aria-hidden="true"
                    className={styles.block}
                    // The rule guards against state bleeding between rows after a reorder. These
                    // blocks are stateless, identical and positional — there is no row identity to
                    // preserve and nothing to reorder, so position IS the identity here.
                    // biome-ignore lint/suspicious/noArrayIndexKey: see the note directly above
                    key={index}
                />
            ))}
        </div>
    );
}
